import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ServicesSection = () => {
  const { features } = useTheme();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.12 });

  const services = [
    {
      title: 'Einzelberatung',
      description: 'Persönliche Unterstützung in herausfordernden Lebensphasen, bei Entscheidungsfindungen oder persönlichen Krisen.',
      points: ['Individuelle Begleitung', 'Ressourcenaktivierung', 'Lösungsorientiert'],
      duration: '60 Min.',
      price: '100€',
      icon: '🌱',
      accent: 'var(--color-primary-500)',
    },
    {
      title: 'Paarberatung',
      description: 'Gemeinsame Wege finden, Kommunikation verbessern und Beziehungsmuster verstehen.',
      points: ['Neutrale Moderation', 'Kommunikationstraining', 'Konfliktlösung'],
      duration: '90 Min.',
      price: '150€',
      icon: '💑',
      accent: 'var(--color-accent-500)',
    },
    {
      title: 'Online-Beratung',
      description: 'Flexible Beratung über sichere Videoplattformen, ortsunabhängig und zeitsparend.',
      points: ['Ortsunabhängig', 'Flexible Termine', 'Datenschutz-konform'],
      duration: '60 Min.',
      price: '100€',
      icon: '💻',
      accent: 'var(--color-secondary-500)',
    },
    {
      title: 'Krisenintervention',
      description: 'Schnelle Hilfe in akuten Krisensituationen mit stabilisierender Begleitung.',
      points: ['Kurzfristige Termine', 'Stabilisierung', 'Notfallplan'],
      duration: '60–90 Min.',
      price: 'n. V.',
      icon: '🆘',
      accent: 'var(--color-accent-500)',
    },
  ];

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.querySelector(id)?.scrollIntoView({ behavior: features.smoothScroll ? 'smooth' : 'auto' });
  };

  return (
    <section id="services" className="py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Mein Beratungsangebot
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Jeder Mensch und jede Situation bringen eigene Anliegen mit. Deshalb biete ich unterschiedliche Beratungsformate an, die sich an Ihren individuellen Bedürfnissen orientieren.
            </p>
            {/* ← section-divider: Farbe via --divider-color in index.css */}
            <div className="section-divider" />
          </div>

          {/* Service Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 overflow-x-hidden">
            {services.map((service) => (
              <div
                key={service.title}
                className="surface card-hover p-4 sm:p-6 flex flex-col"
                style={{
                  borderRadius: 'var(--radius-xl)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Akzentleiste oben */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0,
                    height: 4,
                    backgroundColor: service.accent,
                    opacity: 0.8,
                  }}
                />

                <div className="flex justify-between items-start mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-wider opacity-50">{service.duration}</span>
                </div>

                <h3 className="font-semibold text-lg mb-3" style={{ color: 'var(--color-text-primary)' }}>
                  {service.title}
                </h3>

                <p className="text-sm leading-relaxed mb-5 flex-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {service.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {service.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: service.accent }} />
                      {p}
                    </li>
                  ))}
                </ul>

                <div className="pt-4 flex justify-between items-center" style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
                  <span className="text-xs opacity-50">Kosten</span>
                  <span className="font-semibold text-sm">{service.price}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Info & CTA */}
          <div className="mt-16 max-w-3xl mx-auto">
            <div
              className="p-8 text-center mb-12"
              style={{
                borderRadius: 'var(--radius-xl)',
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-subtle)',
              }}
            >
              <h4 className="font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>Soziale Staffelung</h4>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                Finanzielle Gründe sollen kein Hindernis für professionelle Unterstützung sein.
                Deshalb biete ich für Menschen mit geringem Einkommen reduzierte Preise an.
                Sprechen Sie mich gerne an, wir finden gemeinsam eine passende Lösung.
              </p>
            </div>

            <div className="text-center">
              <p className="mb-6" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                Unsicher, welches Angebot für Sie passt?<br />
                <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                  Im Erstgespräch klären wir gemeinsam, was Sie gerade brauchen.
                </span>
              </p>
              <a
                href="#contact"
                onClick={scrollTo('#contact')}
                className="btn btn-accent inline-flex px-8"
                style={{ borderRadius: 'var(--radius-pill)' }}
              >
                Erstgespräch vereinbaren
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;